// Shared frame for the store-scoped storefront screens (Online store, Settings → Store profile,
// Customers → Inbox): permission gate, loading/error states and the store IDs from the shell.
import type { ReactNode } from "react";
import { t } from "@ady/i18n";
import { BusyRegion, cx, SkeletonText } from "@ady/ui";
import { useShell } from "../../shell/context";
import { LoadError, SettingsPage } from "../../settings/SettingsPage";

export function useStoreIds() {
  const { membership, store } = useShell();
  return { orgId: membership.org.id, storeId: store.id, membership, store };
}

export function StoreScreen(props: {
  title: string;
  lead?: ReactNode;
  anyOf: string[];
  testId: string;
  canonical: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return <SettingsPage {...props} />;
}

/** Loading → error (with retry) → content, for one query. */
export function Loaded<T>({
  q,
  children,
  lines = 4,
}: {
  q: {
    isPending: boolean;
    isError: boolean;
    error: unknown;
    data: T | undefined;
    refetch: () => unknown;
  };
  children: (data: T) => ReactNode;
  lines?: number;
}) {
  if (q.isError) return <LoadError error={q.error} onRetry={() => void q.refetch()} />;
  return (
    <BusyRegion
      busy={q.isPending}
      skeleton={<SkeletonText lines={lines} />}
      data-testid={q.isPending ? "loading-state" : undefined}
    >
      {q.data !== undefined ? children(q.data) : null}
    </BusyRegion>
  );
}

/** D-038 setting row: title and current value on the left, an optional preview, the control right. */
export function SettingRow({
  title,
  value,
  help,
  note,
  error,
  aside,
  wide,
  children,
}: {
  title: string;
  value?: ReactNode;
  help?: string;
  note?: string;
  error?: string;
  aside?: ReactNode;
  /** Gives the control half the row (text inputs). */
  wide?: boolean;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 rounded-xs border border-card bg-surface p-4 md:flex-row md:items-center">
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-sm font-semibold">{title}</span>
        {value ? <span className="ady-user-content text-sm">{value}</span> : null}
        {help ? <span className="text-xs text-fg-tertiary">{help}</span> : null}
        {error ? (
          <span className="text-xs text-danger-fg">{error}</span>
        ) : note ? (
          <span className="text-xs text-fg-tertiary">{note}</span>
        ) : null}
      </div>
      {aside ? <div className="hidden flex-1 justify-center lg:flex">{aside}</div> : null}
      {children ? (
        <div className={cx("flex-none", wide ? "md:w-1/2" : "md:w-56")}>{children}</div>
      ) : null}
    </div>
  );
}

/** Ten tints and shades of a colour, light to dark (the Appearance preview). */
export function ColorScale({ hex }: { hex: string }) {
  const steps = [12, 22, 35, 50, 70, 100, 85, 70, 55, 40];
  return (
    <span className="flex gap-1.5" aria-hidden="true">
      {steps.map((pct, i) => (
        <span
          key={i}
          className="size-6 rounded-full"
          ref={(el) =>
            el?.style.setProperty(
              "background",
              i < 6
                ? `color-mix(in srgb, ${hex} ${pct}%, white)`
                : `color-mix(in srgb, ${hex} ${pct}%, black)`,
            )
          }
        />
      ))}
    </span>
  );
}

export const textareaClass =
  "w-full rounded-sm border border-control bg-surface px-3 py-2 text-md text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus";

export const sectionTypeLabel = (type: string) =>
  t(`storefront.admin.sections.types.${type as "hero"}`);
